import { App, Skeleton } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { kbKeys, useKbOverview } from '@entities/knowledge';
import { watchKb } from '@features/kb-watch';
import { sourceApi } from '@entities/source';
import { extractErrorMessage } from '@shared/api';
import { IconGlobe } from '@shared/ui/v2';
import { useEffect } from 'react';
import { money, when } from './kbUi';
import './knowledge.css';
import './site.css';

function shortUrl(u: string) {
  return u.replace(/^https?:\/\//, '').replace(/\/$/, '') || u;
}

/** Экран 5б-2 «Страница сайта» — во время обработки и когда готов (утверждён 28.10.2026). */
export function SitePage() {
  const { id = '', sourceId = '' } = useParams<{ id: string; sourceId: string }>();
  const { modal, message } = App.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: ov } = useKbOverview(id);
  const src = ov?.sources.find((s) => s.id === sourceId);
  const running = src?.state === 'processing' || src?.state === 'queued';
  useEffect(() => {
    if (running) watchKb(id, [sourceId]);
  }, [running, id, sourceId]);
  const { data: pages } = useQuery({
    queryKey: ['kb', 'site-pages', sourceId],
    queryFn: () => sourceApi.getSourcePages(sourceId),
    enabled: !!sourceId,
    refetchInterval: running ? 3000 : false,
  });

  if (!ov) return <Skeleton active style={{ padding: 24 }} />;
  if (!src) {
    return (
      <div className="v2-content">
        <div className="v2-card v2-card-pad">
          Источник не найден.{' '}
          <Link to={`/assistants/${id}/knowledge`}>Вернуться к базе знаний</Link>
        </div>
      </div>
    );
  }

  const list = pages?.pages ?? [];
  const totalPages = Math.max(src.pages_total ?? src.site_pages ?? list.length, list.length);
  const failed = src.state === 'error';
  const waiting = src.state === 'awaiting';
  const done = running
    ? Math.max(src.pages_done ?? list.length, list.length)
    : failed || waiting
      ? list.length
      : totalPages;
  const left = Math.max(0, totalPages - done);
  // Обработанные страницы без текста или те, что не открылись (не оплачиваются).
  const noText = Math.max(0, done - list.length);
  const pct = running ? (src.percent ?? 0) : 100;
  const etaMin = Math.max(1, Math.round((left * 3) / 60));

  const del = () =>
    modal.confirm({
      title: 'Удалить сайт из базы знаний?',
      content:
        'Ассистент перестанет отвечать по страницам сайта. Деньги за обработку не возвращаются.',
      okText: 'Удалить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await sourceApi.delete(sourceId);
          qc.invalidateQueries({ queryKey: kbKeys.overview(id) });
          navigate(`/assistants/${id}/knowledge`, { replace: true });
        } catch (e) {
          message.error(extractErrorMessage(e));
        }
      },
    });

  return (
    <>
      <header className="v2-topbar is-section">
        <Link to={`/assistants/${id}/knowledge`} className="mp-back">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          База знаний
        </Link>
        <span style={{ color: '#D1D5DB' }}>/</span>
        <h1>{src.display_name || shortUrl(src.name ?? '')}</h1>
      </header>
      <div className="v2-content is-section">
        <section className="v2-card site-hero">
          <span
            className="kb-type"
            style={{ background: '#EFF6FF', color: '#1D4FA8', width: 38, height: 38 }}
          >
            <IconGlobe size={18} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700 }}>{src.display_name || shortUrl(src.name ?? '')}</div>
            <div className="v2-xs v2-muted">
              {src.name} ·{' '}
              {running ? `запущено ${when(src.created_at)}` : `добавлен ${when(src.created_at)}`}
            </div>
          </div>
          {running ? (
            <span className="kb-pill is-blue">Обработка · {pct}%</span>
          ) : src.state === 'ready' ? (
            <>
              <span className="kb-pill is-ok">Готов, ассистент использует</span>
              <Link to={`/assistants/${id}/test-chat`} className="v2-btn is-primary">
                Спросить в тест-чате
              </Link>
              <button type="button" className="v2-btn kb-danger" onClick={del}>
                Удалить
              </button>
            </>
          ) : waiting ? (
            <>
              <span className="kb-pill is-orange">Ждёт оплаты</span>
              <Link to="/billing?topup=1" className="v2-btn is-primary">
                Пополнить баланс
              </Link>
              <button type="button" className="v2-btn kb-danger" onClick={del}>
                Удалить
              </button>
            </>
          ) : (
            <>
              <span className="kb-pill is-red">Ошибка</span>
              <button type="button" className="v2-btn kb-danger" onClick={del}>
                Удалить
              </button>
            </>
          )}
        </section>
        <div className="kb-grid">
          <section
            className="v2-card kb-table-card"
            aria-labelledby="h-pages"
            style={{ paddingBottom: 0 }}
          >
            <div className="site-pages-head">
              <h2 id="h-pages" className="v2-h2">
                {waiting
                  ? `Страницы · ${src.est_pages ?? 0} к обработке`
                  : failed
                    ? `Страницы · ${list.length}`
                    : `Страницы · ${done} из ${totalPages}`}
              </h2>
              <span className="v2-xs v2-muted">
                Фрагмент — кусок текста, по которому ассистент ищет ответ
              </span>
            </div>
            <div className="v2-table-wrap">
              <table className="v2-table">
                <thead>
                  <tr>
                    <th scope="col">Страница</th>
                    <th scope="col">Статус</th>
                    <th scope="col" style={{ textAlign: 'right' }}>
                      Фрагментов
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((p) => (
                    <tr key={p.url}>
                      <td className="v2-wrap" style={{ wordBreak: 'break-all' }}>
                        {shortUrl(p.url)}
                      </td>
                      <td>
                        <span className="site-dot is-ok" /> Готово
                      </td>
                      <td style={{ textAlign: 'right' }}>{p.chunks_count}</td>
                    </tr>
                  ))}
                  {noText > 0 && (
                    <tr>
                      <td colSpan={3} className="v2-muted">
                        <span className="site-dot" /> Без текста или не открылись: {noText} — не
                        оплачиваются
                      </td>
                    </tr>
                  )}
                  {running && left > 0 && (
                    <tr>
                      <td colSpan={3} className="v2-muted">
                        <span className="site-dot is-blue" /> Обрабатываем · ещё {left} в очереди
                      </td>
                    </tr>
                  )}
                  {!list.length && !running && (
                    <tr>
                      <td colSpan={3} className="v2-muted">
                        {waiting
                          ? 'Страницы появятся после обработки — она начнётся после оплаты.'
                          : 'Страниц с текстом нет.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
          <div className="site-side">
            {running && (
              <section className="v2-card kb-aside" style={{ maxWidth: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                  <span>Страницы</span>
                  <span>
                    {done} из {totalPages || '…'}
                  </span>
                </div>
                <div className="kb-bar" style={{ height: 6 }}>
                  <div style={{ width: `${pct}%`, height: 6 }} />
                </div>
                <div className="v2-xs v2-muted">
                  Осталось около {etaMin} мин. Страницу можно закрыть — пришлём уведомление, когда
                  база знаний будет готова.
                </div>
              </section>
            )}
            <section className="v2-card kb-aside" style={{ maxWidth: 'none' }}>
              <h2 className="v2-h2">Стоимость</h2>
              {running ? (
                <>
                  <div className="up-sum-row" style={{ color: '#1F2937' }}>
                    <span>Зарезервировано</span>
                    <b>{money(Number(src.reserved_rub) || src.estimate_rub)}</b>
                  </div>
                  <div className="v2-xs v2-muted">
                    Спишем по факту за обработанные страницы. Если не получится — вернём всё.
                  </div>
                </>
              ) : waiting ? (
                <>
                  <div className="up-sum-total">
                    <span>Спишем не больше</span>
                    <span>{money(src.estimate_rub)}</span>
                  </div>
                  <div className="v2-xs v2-muted">
                    Обработка начнётся сразу после пополнения баланса.
                  </div>
                </>
              ) : failed ? (
                <>
                  <div className="up-sum-total">
                    <span>Итого списано</span>
                    <span>{money(src.charged_rub)}</span>
                  </div>
                  <div className="v2-xs v2-muted">
                    {Number(src.reserved_rub) > 0
                      ? `Резерв ${money(src.reserved_rub)} вернём автоматически.`
                      : 'Деньги за сайт не списаны.'}
                  </div>
                </>
              ) : (
                <>
                  <div className="up-sum-row">
                    <span>Текст · {list.length} стр. × 0,5 ₽</span>
                    <span>{money(list.length * 0.5)}</span>
                  </div>
                  <div className="up-sum-total">
                    <span>Итого списано</span>
                    <span>{money(src.charged_rub)}</span>
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
